
import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const Mesh = 'mesh' as any;
const BoxGeometry = 'boxGeometry' as any;
const MeshStandardMaterial = 'meshStandardMaterial' as any;
const Group = 'group' as any;

// Tech configurations for textures
const TECH_GROUPS = [
  { color: '#ff4400', icons: ['AWS', 'Rust', 'Redis', 'Go', 'Swift'] },
  { color: '#ff00ff', icons: ['Git', 'Java', 'Next', 'Vue', 'Nuxt'] },
  { color: '#00ffff', icons: ['React', 'TS', 'K8s', 'GCP', 'Azure'] },
  { color: '#ffff00', icons: ['JS', 'Py', 'Vite', 'HTML', 'CSS'] },
  { color: '#0088ff', icons: ['Node', 'Docker', 'DB', 'Nginx', 'Kafka'] },
  { color: '#00ff00', icons: ['Django', 'C++', 'GraphQL', 'Nest', 'Fast'] },
];

/**
 * Generates a refined neon texture with better legibility
 */
const generateTechTexture = (text: string, color: string) => {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#020202';
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = color;
  ctx.lineWidth = 16;
  const r = 40;
  const padding = 20;
  
  ctx.beginPath();
  ctx.roundRect(padding, padding, size - padding * 2, size - padding * 2, r);
  
  ctx.shadowColor = color;
  ctx.shadowBlur = 20;
  ctx.stroke();

  ctx.font = `bold ${text.length > 5 ? '100px' : '140px'} Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#000000';
  ctx.fillText(text, size / 2, size / 2);
  
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.fillStyle = color;
  ctx.fillText(text, size / 2, size / 2);

  ctx.shadowBlur = 0;
  ctx.fillText(text, size / 2, size / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 16;
  texture.needsUpdate = true;
  return texture;
};

const coreMaterial = new THREE.MeshStandardMaterial({
  color: '#050505',
  roughness: 0.2,
  metalness: 0.9
});

interface CubieProps {
  position: [number, number, number];
  index: number;
}

const Cubie: React.FC<CubieProps> = ({ position, index }) => {
  const [x, y, z] = position;
  
  const materials = useMemo(() => {
    // Determine which faces are visible
    const faces = [
      { axis: 'x+', visible: x === 1 },
      { axis: 'x-', visible: x === -1 },
      { axis: 'y+', visible: y === 1 },
      { axis: 'y-', visible: y === -1 },
      { axis: 'z+', visible: z === 1 },
      { axis: 'z-', visible: z === -1 }
    ];

    return faces.map((face, faceIdx) => {
      if (!face.visible) return coreMaterial;

      // Seed-based randomization for mixing colors and icons
      // We use index and faceIdx to ensure each face on the cube is unique but deterministic
      const seed = Math.abs(index * 7 + faceIdx * 13 + (x + y + z) * 3);
      const groupIndex = seed % TECH_GROUPS.length;
      const group = TECH_GROUPS[groupIndex];
      const iconIndex = seed % group.icons.length;
      const text = group.icons[iconIndex];
      
      const texture = generateTechTexture(text, group.color);

      return new THREE.MeshStandardMaterial({
        map: texture,
        emissiveMap: texture,
        emissive: new THREE.Color(group.color),
        emissiveIntensity: 1.2,
        roughness: 0.1,
        metalness: 1.0,
      });
    });
  }, [x, y, z, index]);

  return (
    <Mesh position={position} material={materials}>
      <BoxGeometry args={[0.97, 0.97, 0.97]} />
    </Mesh>
  );
};

const RubiksCube: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);
  
  const cubies = useMemo(() => {
    const list = [];
    let i = 0;
    for (let x = -1; x <= 1; x++) {
      for (let y = -1; y <= 1; y++) {
        for (let z = -1; z <= 1; z++) {
          list.push(<Cubie key={`${x}${y}${z}`} position={[x, y, z]} index={i++} />);
        }
      }
    }
    return list;
  }, []);

  useFrame((state) => {
    if (groupRef.current) {
      const time = state.clock.getElapsedTime();
      groupRef.current.rotation.x = Math.sin(time * 0.15) * 0.1 + time * 0.1;
      groupRef.current.rotation.y = Math.cos(time * 0.1) * 0.1 + time * 0.15;
      groupRef.current.position.y = Math.sin(time * 0.5) * 0.15;
      const pulse = 0.9 + Math.sin(time * 1.5) * 0.015;
      groupRef.current.scale.set(pulse, pulse, pulse);
    }
  });

  return (
    <Group ref={groupRef}>
      {cubies}
    </Group>
  );
};

export default RubiksCube;
